import type { StorageFolder, StorageObject } from "../../API";
import type { MediaOrphanReportDto } from "../Checks/checksCommon";

export type UploadKind = "image" | "publication";

export interface UploadFormState {
	title: string;
	fileName: string;
	folder: string;
	taxonomyIds: string[];
	file: File | null;
}

export type ViewMode = "grid" | "list";
export type SortKey = "name" | "size" | "date";
export type SortDir = "asc" | "desc";

export interface BrowseState {
	folder: StorageFolder | null;
	breadcrumbs: StorageFolder[];
	children: StorageFolder[];
	objects: StorageObject[];
	page: number;
	pageSize: number;
	hasMore: boolean;
	totalObjects: number;
}

export type BrowseItem =
	| {
			type: "folder";
			key: string;
			name: string;
			path: string;
			folder: StorageFolder;
	  }
	| {
			type: "file";
			key: string;
			name: string;
			path: string;
			object: StorageObject;
	  };

/** État du scan « médias orphelins », lancé UNE fois à la demande puis mis en cache dans la page. */
export interface UsageScanState {
	report: MediaOrphanReportDto | null;
	loading: boolean;
	error: string | null;
}

export const ROOT_PLACEHOLDER = "__root__";
export const DEFAULT_STORAGE_PATH = ROOT_PLACEHOLDER;
export const ROOT_PATHS: Array<{ path: string; label: string; kind: string; icon: "image" | "document" }> = [
	{ path: "images", label: "Images", kind: "image", icon: "image" },
	{ path: "logo", label: "Logo", kind: "image", icon: "image" },
	{ path: "publications", label: "Publications", kind: "publication", icon: "document" }
];
export const BROWSE_PAGE_SIZE = 100;
export const URL_PATH_PARAM = "path";
export const URL_FILE_PARAM = "file";
export const URL_QUERY_PARAM = "q";
export const URL_TAB_PARAM = "tab";
export const URL_TAXONOMY_PARAM = "tax";

/** Portée du chargement : « all » = tout le bucket (vue racine), « folder » = un dossier précis. */
export type MediaScope = "all" | "folder";

/** Restriction de type de la Médiathèque (mode pick surtout) : pré-filtre onglets, dossiers et fichiers. */
export type MediaAccept = "all" | "images" | "documents";

/** Onglets de la vue racine : chaque onglet filtre par dossier racine côté serveur. */
export type MediaTab = "all" | "images" | "documents" | "logo";
export const MEDIA_TABS: Array<{ id: MediaTab; label: string; folder: string | undefined }> = [
	{ id: "all", label: "Tous", folder: undefined },
	{ id: "images", label: "Images", folder: "images" },
	{ id: "documents", label: "Documents", folder: "publications" },
	{ id: "logo", label: "Logo", folder: "logo" }
];

export const isMediaTab = (value: string | null | undefined): value is MediaTab =>
	MEDIA_TABS.some((tab) => tab.id === value);

export const mediaTabFolder = (tab: MediaTab | undefined): string | undefined =>
	MEDIA_TABS.find((item) => item.id === tab)?.folder;
/** Clé localStorage de la bascule grille/liste : la préférence survit aux sessions. */
export const VIEW_MODE_STORAGE_KEY = "bcc-storage-view-mode";

export const initialFormState: UploadFormState = {
	title: "",
	fileName: "",
	folder: "",
	taxonomyIds: [],
	file: null
};

export const PUBLICATION_ACCEPT = ".pdf,.doc,.docx,.ppt,.pptx,.txt,.rtf";
export const PUBLICATION_EXTENSIONS = new Set(["pdf", "doc", "docx", "ppt", "pptx", "txt", "rtf"]);
